import React, { useEffect, useState, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { Row, Col, message } from "antd";
import "leaflet/dist/leaflet.css";
import blackMarker from "./../../../assets/images/black-marker.svg";
import violetMarker from "./../../../assets/images/violet-marker.svg";
import greenMarker from "./../../../assets/images/green-marker.svg";
import orangeMarker from "./../../../assets/images/orange-marker.svg";
import brownMarker from "./../../../assets/images/brown-marker.svg";
import redMarker from "./../../../assets/images/red-marker.svg";
import blueMarker from "./../../../assets/images/blue-marker.svg";
import lightBrownMarker from "./../../../assets/images/light-brown-marker.svg";
import styles from "./jobListMapView.module.scss";
import { useHistory, useLocation } from "react-router-dom";
import { useWrapperContext } from "../../../context/WrapperContext";
import { JobIconColor, JobStepNumber } from "../../../Utilities/jobUtil";
import { UserRoles } from "../../../enums/UserRolesEnum";
import { ViewListingTypes } from "../../../enums/ViewListingTypes";
import CreateJobServices from "../../../services/createJob.services";
import { CreateJobViewConstants } from "../constants";
import TNLoader from "../../../reusable-components/TNLoader";
import { DispatchStatus } from "../../../enums/DispatchStatusEnum";
import { MapViewLegendConsts } from "./constants";
import { MapConstants } from "../../../constants/MapConstants";

const FitBounds = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    const validLocations = locations?.filter(
      (loc) => loc?.adjustedLat != null && loc?.adjustedLng != null
    );

    if (validLocations && validLocations?.length > 0) {
      const bounds = L?.latLngBounds(
        validLocations?.map((loc) => [loc?.adjustedLat, loc?.adjustedLng])
      );
      map?.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);

  return null;
};

const JobListMapView = ({ filters, noActionAllowed, showBlocked }) => {
  const navigate = useHistory();
  const location = useLocation();
  const { setPrevRecord } = useWrapperContext();
  const { 
    GOOGLE_MAP_ATTRIBUTION,
    GOOGLE_SATILLITE_TILE_LAYER_URL,
    GOOGLE_TILE_SUBDOMAIN_ARRAY
  } = MapConstants;
  const { FetchAllJobMap, GetJobDetails } = CreateJobServices();
  const userDetailsJson = JSON.parse(localStorage.getItem("userDetails"));
  const isSalesPerson =
    userDetailsJson?.["userProfile"] === UserRoles.SALES_PERSON;

  const {
    data: jobMapData,
    isFetching,
    refetch: refetchAllMapJobs,
  } = FetchAllJobMap(
    {
      search: filters?.search,
      jobStatus: filters?.jobStatus,
      salesPersonId: filters?.salesPerson,
      companyId: filters?.customerName,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate,
      isBlocked: showBlocked,
    },
    true
  );
  const { mutateAsync: getSingleJob, isPending } = GetJobDetails();
  const processedLocations = useMemo(() => {
    if (!jobMapData?.data) return [];
    const coordMap = new Map();
    jobMapData?.data?.forEach((location) => {
      const lat = location?.dropOffLatLng?.lat;
      const lng = location?.dropOffLatLng?.lng;
      if (
        lat === null || lng === null
        || isNaN(lat) || isNaN(lng)
        || lat === "" || lng === ""
      ) {
        return;
      }
      const coordKey = `${lat},${lng}`;
      if (!coordMap?.has(coordKey)) {
        coordMap?.set(coordKey, []);
      }
      coordMap?.get(coordKey).push(location);
    });
    const newLocations = [];
    const BASE_OFFSET = 0.001;
    coordMap?.forEach((locations) => {
      const count = locations.length;
      if (count === 1) {
        const loc = locations[0];
        newLocations.push({
          ...loc,
          adjustedLat: loc.dropOffLatLng.lat,
          adjustedLng: loc.dropOffLatLng.lng,
        });
      } else {
        locations.forEach((loc, index) => {
          const angle = (index / count) * (2 * Math.PI);
          const offsetLat = BASE_OFFSET * Math.cos(angle);
          const offsetLng = BASE_OFFSET * Math.sin(angle);
          newLocations.push({
            ...loc,
            adjustedLat: loc.dropOffLatLng.lat + offsetLat,
            adjustedLng: loc.dropOffLatLng.lng + offsetLng,
          });
        });
      }
    });

    return newLocations;
  }, [jobMapData?.data]);

  const createCustomIcon = (L, jobNumber, colorCode) => {
    return L.divIcon({
      className: "custom-marker-icon",
      html:
        "<div class='naviconmapss' style='color: " +
        colorCode +
        "; border: 1px solid " +
        colorCode +
        "; border-radius: 30px; padding:15px;width:100%;height:100%'>" +
        "<i class='fas fa-map-marker'></i>" +
        "<div class='truckid'>" +
        "<p>" +
        jobNumber +
        "</p>" +
        "</div>" +
        "</div>",
    });
  };

  const handleViewNavigate = (record) => {
    navigate.push(
      `/app/view-trucking-material/${record?.id}${window.location?.hash}`
    );
  };

  const handleEditNavigate = async (record) => {
    if (record?.stageId === DispatchStatus?.APPROVED) {
      return;
    }
    await getSingleJob?.(record?.id, {
      onSuccess: (data) => {
        setPrevRecord(data);
        JobStepNumber[data?.stageId] &&
          navigate.push(
            `/app/update-trucking-material/${data?.id}#${ViewListingTypes.MAP}`,
            {
              jobSpecificsData: data,
              edit: true,
              jobData: jobMapData,
              step: JobStepNumber[data?.stageId],
              from: location,
            }
          );
      },
      onError: (err) => {
        console.error("Err", err);
      },
    });
  };

  useEffect(() => {
    if (location.hash?.includes(ViewListingTypes.MAP)) refetchAllMapJobs();
  }, [filters, showBlocked, location.hash, refetchAllMapJobs]);

  return (
    <>
      <TNLoader isLoading={isFetching || isPending} />
      <Row className={styles["mapview"]}>
        <Col span={18}>
          <MapContainer
            center={[31.52, -97.17]}
            zoom={4}
            className={styles.map}
            maxBoundsViscosity={1.0}
            minZoom={2}
            maxBounds={[
              [-90, -180],
              [90, 180],
            ]}
          >
            <TileLayer 
              attribution={GOOGLE_MAP_ATTRIBUTION}
              url={GOOGLE_SATILLITE_TILE_LAYER_URL}
              subdomains={GOOGLE_TILE_SUBDOMAIN_ARRAY}
            />
            {processedLocations?.map((job) => (
              <Marker
                key={job?.id}
                position={[job?.adjustedLat ?? 0, job?.adjustedLng ?? 0]}
                icon={createCustomIcon(
                  L,
                  job?.jobNumber,
                  JobIconColor[job?.stageId]
                )}
                eventHandlers={{
                  click: () => {
                    if(!!!showBlocked) {
                      if (!!noActionAllowed) {
                        if (!isSalesPerson) handleEditNavigate(job);
                      } else {
                        message.error(
                          CreateJobViewConstants.EDIT_ACCESS_REQUIRED
                        );
                      }
                    } else {
                      handleViewNavigate(job);
                    }
                  },
                }}
              >
                <Tooltip direction="top" offset={[10, -10]} opacity={1}>
                  <div className="p-2">
                    <h3 className="font-bold text-lg">{job?.jobNumber}</h3>
                    <p className="text-gray-700">{job?.jobType}</p>
                    <p className="text-gray-700">{job?.companyName}</p>
                  </div>
                </Tooltip>
              </Marker>
            ))}
            <FitBounds locations={processedLocations} />
          </MapContainer>
        </Col>

        <Col span={6} className={styles["info-column"]}>
          <Row className={styles["info-card"]}>
            <Col span={3}>
              <img src={blackMarker} alt={MapViewLegendConsts.LEAD_MARKER_ALT} />
            </Col>
            <Col>
              <span>{MapViewLegendConsts.JOB_LEAD}</span>
            </Col>
          </Row>
          <Row className={styles["info-card"]}>
            <Col span={3}>
              <img src={violetMarker} alt={MapViewLegendConsts.MATERIAL_AND_TRUCKING_DETAILS_ALT} />
            </Col>
            <Col>
              <span>{MapViewLegendConsts.MATERIAL_AND_TRUCKING_DETAILS}</span>
            </Col>
          </Row>
          <Row className={styles["info-card"]}>
            <Col span={3}>
              <img src={greenMarker} alt={MapViewLegendConsts.BUILD_QUOTE_ALT} />
            </Col>
            <Col>
              <span>{MapViewLegendConsts.BUILD_QUOTE}</span>
            </Col>
          </Row>
          <Row className={styles["info-card"]}>
            <Col span={3}>
              <img src={orangeMarker} alt={MapViewLegendConsts.QUOTE_SHARED_ALT} />
            </Col>
            <Col>
              <span>{MapViewLegendConsts.QUOTE_SHARED}</span>
            </Col>
          </Row>
          <Row className={styles["info-card"]}>
            <Col span={3}>
              <img src={brownMarker} alt={MapViewLegendConsts.QUOTE_APPROVED_ALT} />
            </Col>
            <Col>
              <span>{MapViewLegendConsts.QUOTE_APPROVED}</span>
            </Col>
          </Row>
          <Row className={styles["info-card"]}>
            <Col span={3}>
              <img src={redMarker} alt={MapViewLegendConsts.INTERNAL_APPROVAL_ALT} />
            </Col>
            <Col>
              <span>{MapViewLegendConsts.INTERNAL_APPROVAL}</span>
            </Col>
          </Row>
          <Row className={styles["info-card"]}>
            <Col span={3}>
              <img src={lightBrownMarker} alt={MapViewLegendConsts.INTERNAL_APPROVAL_DRAFT_ALT} />
            </Col>
            <Col>
              <span>{MapViewLegendConsts.INTERNAL_APPROVAL_DRAFT}</span>
            </Col>
          </Row>
          <Row className={styles["info-card"]}>
            <Col span={3}>
              <img src={blueMarker} alt={MapViewLegendConsts.DISPATCH_APPROVED_ALT} />
            </Col>
            <Col>
              <span>{MapViewLegendConsts.DISPATCH_APPROVED}</span>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default JobListMapView;
